import React, { useEffect, useRef, useState } from "react";
import { useWM } from "./store";

/**
 * HEXA Gesture & Hotkey layer.
 *
 * Hotkeys (in any focus context):
 *   Alt+Tab            → cycle to next open window
 *   Alt+Shift+Tab      → cycle to previous window
 *   Ctrl/Cmd+,         → toggle gesture HUD cheat sheet
 *
 * Trackpad gestures:
 *   Two-finger horizontal swipe on the empty desktop (deltaX, no ctrl)
 *                      → cycle window focus left / right
 *   Pinch / Ctrl+Wheel on a window  → small scale animation feedback (visual cue)
 *
 * Strategy: minimal, no camera required. Works on Mac/Windows/Linux trackpads.
 * Gestures fire briefly then return to normal interaction.
 */
export default function GestureLayer() {
  const { windows, focused, focus } = useWM();
  const [hint, setHint] = useState("");
  const hintTimer = useRef(null);
  const lastSwipe = useRef(0);

  const flashHint = (msg) => {
    setHint(msg);
    if (hintTimer.current) clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => setHint(""), 1100);
  };

  const cycle = (dir) => {
    if (!windows || windows.length < 2) return;
    const ordered = [...windows].sort((a, b) => (a.minimized ? 1 : -1) - (b.minimized ? 1 : -1));
    const idx = ordered.findIndex(w => w.id === focused);
    const next = ordered[(idx + dir + ordered.length) % ordered.length];
    if (next) {
      focus(next.id);
      flashHint(`→ ${next.title}`);
    }
  };

  // --- Keyboard hotkeys ---
  useEffect(() => {
    const onKey = (e) => {
      // Alt+Tab cycler
      if (e.altKey && e.key === "Tab") {
        e.preventDefault();
        cycle(e.shiftKey ? -1 : 1);
        return;
      }
      // Cmd/Ctrl + Comma → cheat sheet toggle
      if ((e.metaKey || e.ctrlKey) && e.key === ",") {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("hexa-gesture-help"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [windows, focused]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Trackpad gesture detection (wheel events) ---
  useEffect(() => {
    const onWheel = (e) => {
      // Pinch (ctrlKey set on wheel = trackpad pinch)
      if (e.ctrlKey) {
        flashHint(e.deltaY < 0 ? "⌖ zoom in" : "⌖ zoom out");
        return; // let browser handle the rest
      }
      // Two-finger horizontal swipe on the bare desktop (not over an interactive surface)
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag)) return;
      if (e.target?.closest("[data-window]")) return; // ignore wheel inside windows
      if (Math.abs(e.deltaX) > 40 && Math.abs(e.deltaX) > Math.abs(e.deltaY) * 1.5) {
        const now = Date.now();
        if (now - lastSwipe.current < 600) return;
        lastSwipe.current = now;
        cycle(e.deltaX > 0 ? 1 : -1);
      }
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => window.removeEventListener("wheel", onWheel);
  }, [windows, focused]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hint) return null;
  return (
    <div
      data-testid="gesture-hint"
      className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[700] pointer-events-none"
      style={{
        background: "rgba(11,16,20,0.92)",
        border: "1px solid rgba(139,92,246,0.5)",
        padding: "8px 16px",
        clipPath: "polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))",
        animation: "gesture-pulse 0.9s ease-out",
      }}
    >
      <span className="mono text-[11px] uppercase tracking-[0.25em] text-[#8b5cf6]">{hint}</span>
      <style>{`
        @keyframes gesture-pulse {
          0% { transform: translate(-50%, 10px) scale(0.92); opacity: 0; }
          25% { transform: translate(-50%, 0) scale(1.02); opacity: 1; }
          100% { transform: translate(-50%, 0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
